/** @jsxImportSource @emotion/react */
'use client';

import React from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import type { SpinnerProps, SpinnerSize } from './types';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SIZE_MAP: Record<SpinnerSize, string> = {
  xs: '12px',
  sm: '16px',
  md: '24px',
  lg: '32px',
};

const StyledSpinner = styled.span<{ $size: SpinnerSize }>`
  display: inline-block;
  box-sizing: border-box;
  width: ${({ $size }) => SIZE_MAP[$size]};
  height: ${({ $size }) => SIZE_MAP[$size]};
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  flex-shrink: 0;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-right-color: currentColor;
    opacity: 0.5;
  }
`;

export const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(
  (
    {
      size = 'md',
      label = 'Loading',
      className,
      testId = 'spinner',
      css,
      style,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledSpinner
        ref={ref}
        role="status"
        aria-label={label}
        data-testid={testId}
        data-size={size}
        $size={size}
        className={className}
        css={css}
        style={style}
        {...rest}
      />
    );
  }
);

Spinner.displayName = 'Spinner';
